import { nanoid } from "nanoid";
import { useCallback, useEffect, useRef, useState } from "react";
import type { AIProviderId } from "../../shared/aiProviders";
import {
  IMAGE_TYPES,
  maxImageDimensionFor,
} from "../../shared/attachmentPolicy";
import {
  attachmentSizeLimit,
  MAX_ATTACHMENTS,
  type PendingAttachment,
  resolveAttachmentMediaType,
  UploadError,
  uploadFile,
} from "../lib/attachments";
import { attachmentUploadPath } from "../lib/apiPaths";
import { emitErrorToast } from "../lib/errorToast";
import { fitImage } from "../lib/imageProcessing";

function isZip(file: File) {
  return file.name.toLowerCase().endsWith(".zip");
}

// A canvas re-encode would keep only a GIF's first frame.
function isResizableImage(mediaType: string) {
  return IMAGE_TYPES.has(mediaType) && mediaType !== "image/gif";
}

function rejectionToast(message: string) {
  emitErrorToast({ icon: "paperclip", message, buttonLabel: "Dismiss" });
}

function sizeLimitLabel(mediaType: string): string {
  const max = attachmentSizeLimit(mediaType);
  const limitMb = Math.floor((max / (1024 * 1024)) * 10) / 10;
  const kind = IMAGE_TYPES.has(mediaType)
    ? "Images"
    : mediaType === "application/pdf"
      ? "PDFs"
      : "Files of this type";
  return `${kind} must be under ${limitMb} MB.`;
}

function tooLargeToast(fileName: string, sizeBytes: number, mediaType: string) {
  const actualMb = Math.round((sizeBytes / (1024 * 1024)) * 10) / 10;
  rejectionToast(
    `${fileName} is too large (${actualMb} MB). ${sizeLimitLabel(mediaType)}`
  );
}

export function useFileDropzone({
  enabled = true,
  providerId,
  onZipFile,
}: {
  enabled?: boolean;
  providerId: AIProviderId;
  onZipFile?: (file: File) => void;
}) {
  const [pendingAttachments, setPendingAttachments] = useState<
    PendingAttachment[]
  >([]);
  const pendingRef = useRef(pendingAttachments);
  pendingRef.current = pendingAttachments;
  const [isDragging, setIsDragging] = useState(false);
  const dragCounterRef = useRef(0);
  const [flyingFiles, setFlyingFiles] = useState<
    { id: string; x: number; y: number }[]
  >([]);

  const removeAttachment = useCallback((id: string) => {
    setPendingAttachments((prev) => {
      const att = prev.find((a) => a.id === id);
      if (att) URL.revokeObjectURL(att.previewUrl);
      return prev.filter((a) => a.id !== id);
    });
  }, []);

  const patchAttachment = useCallback(
    (id: string, patch: Partial<PendingAttachment>) => {
      setPendingAttachments((prev) =>
        prev.map((a) => (a.id === id ? { ...a, ...patch } : a))
      );
    },
    []
  );

  const uploadAttachment = useCallback(
    async (id: string, file: File, sourceMediaType: string) => {
      const limit = attachmentSizeLimit(sourceMediaType);
      let blob: Blob = file;
      let mediaType = sourceMediaType;
      if (isResizableImage(sourceMediaType)) {
        const fitted = await fitImage(file, {
          maxDimension: maxImageDimensionFor(providerId),
          maxBytes: limit,
          mediaType: sourceMediaType,
        });
        if (fitted) {
          blob = fitted.blob;
          mediaType = fitted.mediaType;
        }
      }
      if (blob.size > limit) {
        removeAttachment(id);
        tooLargeToast(file.name, blob.size, sourceMediaType);
        return;
      }
      const remoteId = await uploadFile(
        attachmentUploadPath(),
        blob,
        mediaType
      );
      patchAttachment(id, { remoteId, mediaType, sizeBytes: blob.size });
    },
    [providerId, removeAttachment, patchAttachment]
  );

  const processFiles = useCallback(
    (files: File[]) => {
      const zip = onZipFile && files.find(isZip);
      if (zip) onZipFile(zip);
      const rest = zip ? files.filter((f) => f !== zip) : files;
      const accepted: PendingAttachment[] = [];
      let hitLimit = false;
      for (const file of rest) {
        if (pendingRef.current.length + accepted.length >= MAX_ATTACHMENTS) {
          hitLimit = true;
          break;
        }
        const mediaType = resolveAttachmentMediaType(file, providerId);
        if (!mediaType) {
          rejectionToast(
            `${file.name} can’t be attached — that file type isn’t supported.`
          );
          continue;
        }
        // Oversized resizable images are shrunk in uploadAttachment, not refused.
        if (
          !isResizableImage(mediaType) &&
          file.size > attachmentSizeLimit(mediaType)
        ) {
          tooLargeToast(file.name, file.size, mediaType);
          continue;
        }
        const isDuplicate = [...pendingRef.current, ...accepted].some(
          (a) =>
            a.fileName === file.name &&
            a.sizeBytes === file.size &&
            a.mediaType === mediaType
        );
        if (isDuplicate) continue;
        const id = nanoid(12);
        void uploadAttachment(id, file, mediaType).catch((error) => {
          rejectionToast(
            error instanceof UploadError &&
              (error.status === 413 || error.status === 415)
              ? `${file.name} couldn’t be uploaded. ${error.message}`
              : `${file.name} couldn’t be uploaded.`
          );
          removeAttachment(id);
        });
        accepted.push({
          id,
          fileName: file.name,
          mediaType,
          sizeBytes: file.size,
          previewUrl: URL.createObjectURL(file),
          remoteId: null,
        });
      }
      if (hitLimit) {
        rejectionToast(
          `You can attach up to ${MAX_ATTACHMENTS} files per message.`
        );
      }
      if (accepted.length === 0) return;
      setPendingAttachments((prev) => [...prev, ...accepted]);
    },
    [providerId, onZipFile, removeAttachment, uploadAttachment]
  );

  const isUploading = pendingAttachments.some((a) => a.remoteId == null);

  const clearAttachments = useCallback(() => {
    setPendingAttachments((prev) => {
      for (const att of prev) URL.revokeObjectURL(att.previewUrl);
      return [];
    });
  }, []);

  useEffect(() => {
    if (!enabled) return;
    const onDragEnter = (e: DragEvent) => {
      e.preventDefault();
      if (e.dataTransfer?.types.includes("Files")) {
        dragCounterRef.current++;
        if (dragCounterRef.current === 1) setIsDragging(true);
      }
    };
    const onDragLeave = (e: DragEvent) => {
      e.preventDefault();
      dragCounterRef.current--;
      if (dragCounterRef.current === 0) setIsDragging(false);
    };
    const onDragOver = (e: DragEvent) => e.preventDefault();
    const onDrop = (e: DragEvent) => {
      e.preventDefault();
      dragCounterRef.current = 0;
      setIsDragging(false);
      const files = Array.from(e.dataTransfer?.files ?? []);
      const hasValid = files.some(
        (f) => resolveAttachmentMediaType(f, providerId) != null
      );
      if (hasValid) {
        setFlyingFiles((prev) => [
          ...prev,
          { id: nanoid(6), x: e.clientX, y: e.clientY },
        ]);
      }
      processFiles(files);
    };
    window.addEventListener("dragenter", onDragEnter);
    window.addEventListener("dragleave", onDragLeave);
    window.addEventListener("dragover", onDragOver);
    window.addEventListener("drop", onDrop);
    return () => {
      window.removeEventListener("dragenter", onDragEnter);
      window.removeEventListener("dragleave", onDragLeave);
      window.removeEventListener("dragover", onDragOver);
      window.removeEventListener("drop", onDrop);
    };
  }, [enabled, processFiles, providerId]);

  const removeFlyingFile = useCallback((id: string) => {
    setFlyingFiles((prev) => prev.filter((f) => f.id !== id));
  }, []);

  const handlePaste = useCallback(
    (e: React.ClipboardEvent) => {
      const items = Array.from(e.clipboardData.items);
      const files: File[] = [];
      for (const item of items) {
        if (item.kind !== "file") continue;
        const file = item.getAsFile();
        if (!file) continue;
        if (
          resolveAttachmentMediaType(file, providerId) != null ||
          (onZipFile && isZip(file))
        ) {
          files.push(file);
        }
      }
      if (files.length > 0) {
        e.preventDefault();
        processFiles(files);
      }
    },
    [processFiles, providerId, onZipFile]
  );

  return {
    pendingAttachments,
    isUploading,
    isDragging,
    flyingFiles,
    processFiles,
    removeAttachment,
    clearAttachments,
    removeFlyingFile,
    handlePaste,
  };
}
